"use client";

import type { ProductCard as ProductCardData } from "@ellaandil/formatters";
import { Add } from "@ellaandil/ui";
import { type ComponentProps, useState } from "react";
import { Button } from "@/components/ui/Button";
import { ProductCard } from "@/components/ui/ProductCard";
import { Typography } from "@/components/ui/Typography";
import { QuickAddSlot } from "@/features/cart/QuickAddSlot";
import { useTranslations } from "@/i18n/client";
import { type CarouselLayout, CarouselTrack } from "./_lib/CarouselTrack";

export type RecommendedProduct = ProductCardData & {
	depictListingResultId?: string;
	depictProductId?: string;
};

export interface RecommendationsCarouselProps {
	products: RecommendedProduct[];
	heading: string;
	/** Defaults to "triplets" — the cart drawer's narrow 3-up layout. */
	layout?: CarouselLayout;
	/** Defaults to "body-sm-reg" — the cart drawer's heading style. */
	headingVariant?: ComponentProps<typeof Typography>["variant"];
	/** Defaults to "small" — only "large" renders the product name at the base font size. */
	cardSize?: ComponentProps<typeof ProductCard>["size"];
	/**
	 * Defaults to `true`. The cart drawer wants an inline [+ ADD] on each card
	 * so customers can add an upsell without leaving the drawer; the PDP just
	 * links to the product like any other card, so it turns this off.
	 */
	quickAdd?: boolean;
	/** Defaults to `true`. The cart drawer's narrow cards turn this off. */
	showSwatches?: boolean;
	/**
	 * Defaults to `false`. The cart drawer turns this on so every card in a
	 * row matches height regardless of a 1- vs 2-line product name.
	 */
	clampName?: boolean;
}

/**
 * Depict-powered recommendations carousel. Shared between the cart drawer's
 * `after_basket` upsell (`CartUpsell`) and the PDP's `related` rail
 * (`ProductRecommendations`) — same carousel and tracking, different Depict
 * recommendation `type` feeding it.
 *
 * When `quickAdd` is on, tapping a card's [+ ADD] reveals an inline
 * `QuickAddPanel` so customers can pick colour + size without leaving the
 * page; the actual add-to-cart goes through the shared buy-box flow on PDP.
 */
export const RecommendationsCarousel = ({
	products,
	heading,
	layout = "triplets",
	headingVariant = "body-sm-reg",
	cardSize = "small",
	quickAdd = true,
	showSwatches = true,
	clampName = false,
}: RecommendationsCarouselProps) => {
	const [quickAddId, setQuickAddId] = useState<string | null>(null);
	const quickAddProduct =
		quickAdd && quickAddId ? products.find((p) => p.id === quickAddId) : undefined;

	// Skip the skeleton state entirely — Depict may legitimately return zero
	// results (no rule configured for the current context), and flashing a
	// skeleton row only to vanish a moment later reads as a bug. Once data
	// resolves we either mount cards or stay hidden.
	if (products.length === 0) return null;

	return (
		<section aria-label={heading} className="flex flex-col gap-grid-component-spacing-md">
			<Typography variant={headingVariant} as="h2" className="text-text-dark-text-1">
				{heading}
			</Typography>
			<CarouselTrack
				layout={layout}
				desktopAlwaysCarousel
				slotBeforeScrollbar={
					quickAdd ? (
						<QuickAddSlot product={quickAddProduct} onClose={() => setQuickAddId(null)} />
					) : undefined
				}
			>
				{products.map((product) => (
					<UpsellItem
						key={product.id}
						product={product}
						cardSize={cardSize}
						showSwatches={showSwatches}
						clampName={clampName}
						onAdd={quickAdd ? () => setQuickAddId(product.id ?? null) : undefined}
						isActive={product.id === quickAddId}
					/>
				))}
			</CarouselTrack>
		</section>
	);
};

const UpsellItem = ({
	product,
	cardSize,
	showSwatches,
	clampName,
	onAdd,
	isActive,
}: {
	product: RecommendedProduct;
	cardSize: ComponentProps<typeof ProductCard>["size"];
	showSwatches: boolean;
	clampName: boolean;
	onAdd?: () => void;
	isActive: boolean;
}) => {
	const t = useTranslations();
	return (
		<div className="flex flex-col gap-grid-component-spacing-2xs">
			<ProductCard
				product={product}
				size={cardSize}
				showBrand={false}
				showSwatches={showSwatches}
				clampName={clampName}
			/>
			{onAdd && !isActive && (
				<Button
					variant="outline"
					color="primary"
					size="small"
					onClick={onAdd}
					aria-expanded={isActive}
					className="self-start"
				>
					<Add className="size-3" />
					{t("Add")}
				</Button>
			)}
		</div>
	);
};
